import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdditionalData from './AdditionalData.tsx';
import CustomerLocation from './CustomerLocation.tsx';
import FinalReviewModal from './FinalReviewModal.tsx';
import MedList from './MedList.tsx';
import TradingPartnerSelect from './TradingPartnerSelect.tsx';

import { MedAPI } from '../../common/api/med.ts';
import { VerifiedTradingPartnerAPI } from '../../common/api/verifiedTradingPartner.ts';
import mutationHooks from '../../common/mutationHooks/index.ts';
import queryHooks from '../../common/queryHooks/index.ts';
import {
  CustomerLocation as CustomerLocationModel,
  CustomerLocationAddress,
  TradingPartner,
  TradingPartnerAddress,
  TradingPartnerContact,
} from '../../common/types/models.ts';
import {
  HydratedParsedOutboundDocument,
  ParsedEpcisEvent,
} from '../../common/types/compoundModels.ts';
import { toast } from '../../func.ts';

export interface SendPedigreeData {
  tradingPartnerAddressId?: number;
  verifiedTradingPartnerEligibility?: VerifiedTradingPartnerAPI.Response.ReadOneIsAs2Active | null;
  selectedEmailAddresses: string[];
}

export interface AdditionalSendPedigreeData {
  poNumber: string;
  invoiceNumber: string;
  advanceShipNotice: string;
}

export interface AmendPedigreeFinalReviewData {
  customerLocation?: CustomerLocationModel;
  customerLocationAddress?: CustomerLocationAddress;
  tradingPartner?: TradingPartner;
  tradingPartnerAddress?: TradingPartnerAddress;
  meds?: MedAPI.Response.ReadOneBySerialNumberPedigree[];
  additionalData?: AdditionalSendPedigreeData;
}

const EMPTY_ADDITIONAL_DATA: AdditionalSendPedigreeData = {
  poNumber: '',
  invoiceNumber: '',
  advanceShipNotice: '',
};

const AmendEpcis = () => {
  const navigate = useNavigate();
  const { outboundDocumentId } = useParams();

  const documentId = outboundDocumentId ?? '0';

  const [isBuilding, setIsBuilding] = useState<boolean>(false);
  const [isOpenFinalReviewModal, setIsOpenFinalReviewModal] = useState(false);
  const [selectedEmailAddresses, setSelectedEmailAddresses] = useState<
    string[]
  >([]);
  const [meds, setMeds] = useState<
    MedAPI.Response.ReadOneBySerialNumberPedigree[]
  >([]);
  const [additionalData, setAdditionalData] =
    useState<AdditionalSendPedigreeData>(EMPTY_ADDITIONAL_DATA);

  const { data: parsedOutboundDocument, isLoading: isLoadingParsedDocument } =
    queryHooks.outboundDocument.useParseOneById(documentId);

  const outboundDocument = parsedOutboundDocument?.outboundDocument;
  const tradingPartner = outboundDocument?.tradingPartner;
  const tradingPartnerAddress = outboundDocument?.tradingPartnerAddress;
  const customerLocation = outboundDocument?.customerLocation;
  const customerLocationAddress = outboundDocument?.customerLocationAddress;
  const tradingPartnerContactsWithEmail =
    outboundDocument?.tradingPartnerContactsWithEmail;

  const verifiedTradingPartnerId =
    typeof tradingPartner?.verifiedTradingPartnerId === 'number'
      ? tradingPartner.verifiedTradingPartnerId
      : undefined;

  const { data: verifiedTradingPartnerEligibility } =
    queryHooks.verifiedTradingPartner.useReadOneIsAs2ActiveById(
      verifiedTradingPartnerId
    );

  const customerLocationAddressId = customerLocationAddress?.id ?? undefined;
  const tradingPartnerAddressId = tradingPartnerAddress?.id ?? undefined;

  const getInitialAdditionalData = (
    parsedOutboundDocument: HydratedParsedOutboundDocument
  ) => {
    const events = parsedOutboundDocument?.parsedDocument?.events?.items ?? [];
    const nextData = { ...EMPTY_ADDITIONAL_DATA };

    const observeEvent = events.find(
      (event: ParsedEpcisEvent) => event.action === 'OBSERVE'
    );

    observeEvent?.bizTransactionList?.forEach(
      (transaction: { identifier: string; type: string }) => {
        switch (transaction?.type) {
          case 'po':
            nextData.poNumber = transaction.identifier;
            break;
          case 'inv':
            nextData.invoiceNumber = transaction.identifier;
            break;
          case 'desadv':
            nextData.advanceShipNotice = transaction.identifier;
            break;
        }
      }
    );

    return setAdditionalData(nextData);
  };

  useEffect(() => {
    setMeds(outboundDocument?.meds ?? []);
    const emailList = outboundDocument?.tradingPartnerContactsWithEmail?.map(
      (contact: TradingPartnerContact) =>
        contact?.contactDetails?.[0]?.contactDetail
    );

    if (emailList?.length) {
      setSelectedEmailAddresses(emailList);
    }
  }, [outboundDocument]);

  useEffect(() => {
    getInitialAdditionalData(parsedOutboundDocument);
  }, [parsedOutboundDocument]);

  const finalReviewData = useMemo<AmendPedigreeFinalReviewData>(
    () => ({
      customerLocation,
      customerLocationAddress: customerLocationAddress ?? undefined,
      tradingPartner: (tradingPartner as TradingPartner | null) ?? undefined,
      tradingPartnerAddress: tradingPartnerAddress ?? undefined,
      meds,
      additionalData,
    }),
    [
      customerLocation,
      customerLocationAddress,
      tradingPartner,
      tradingPartnerAddress,
      meds,
      additionalData,
    ]
  );

  const isDisabled =
    !tradingPartnerAddressId ||
    !customerLocationAddressId ||
    meds.length === 0 ||
    (!verifiedTradingPartnerEligibility?.isAs2Active &&
      !selectedEmailAddresses.length);

  const { mutate: sendPedigrees } =
    mutationHooks.med.useCreateManyAmendPedigrees({
      onSuccess: data => {
        if (data) {
          navigate('/sentepcisdocs');
        }
        document.body.style.overflow = 'auto';
      },
      onSettled: () => {
        setIsBuilding(false);
      },
    });

  const handleOpenFinalReviewModal = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (!isDisabled) {
        setIsOpenFinalReviewModal(true);
      }
    },
    [isDisabled]
  );

  const resend = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsBuilding(true);

      if (!outboundDocumentId || !outboundDocument) {
        return toast({
          payload: {
            msg: 'Missing Document',
            class: 'red white-text',
          },
        });
      }

      if (!tradingPartnerAddressId) {
        return toast({
          payload: {
            msg: 'Could not parse Trading Partner Address from EPCIS',
            class: 'red white-text',
          },
        });
      }

      if (!customerLocationAddressId) {
        return toast({
          payload: {
            msg: 'Could not parse Customer Location Address from EPCIS',
            class: 'red white-text',
          },
        });
      }

      const documentIdentifier = outboundDocument.documentIdentifier ?? '';
      const datetimeCreated =
        outboundDocument.outboundDocumentFile?.datetimeCreated ?? '';

      if (!documentIdentifier || !datetimeCreated) {
        return toast({
          payload: {
            msg: 'Missing Document Identifier',
            class: 'red white-text',
          },
        });
      }

      if (!datetimeCreated) {
        return toast({
          payload: {
            msg: 'No previous created document',
            class: 'red white-text',
          },
        });
      }

      sendPedigrees({
        outboundDocumentId: Number(outboundDocumentId),
        tradingPartnerAddressId,
        verifiedTradingPartnerId:
          outboundDocument.tradingPartner?.verifiedTradingPartnerId,
        medIds: [...new Set(meds.map(({ med }) => med.id))],
        recipientEmailAddresses: selectedEmailAddresses,
        customerLocationAddressId,
        ...additionalData,
        documentIdentifier,
        datetimeCreated,
      });
    },
    [
      outboundDocumentId,
      outboundDocument,
      tradingPartnerAddressId,
      customerLocationAddressId,
      sendPedigrees,
      meds,
      selectedEmailAddresses,
      additionalData,
    ]
  );

  if (isLoadingParsedDocument) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div className="preloader-wrapper active">
            <div className="spinner-layer spinner-blue-only">
              <div className="circle-clipper left">
                <div className="circle" />
              </div>
              <div className="gap-patch">
                <div className="circle" />
              </div>
              <div className="circle-clipper right">
                <div className="circle" />
              </div>
            </div>
          </div>
          Parsing Document . . .
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row">
        <CustomerLocation customerLocationAddress={customerLocationAddress} />
      </div>

      <div className="row">
        <TradingPartnerSelect
          tradingPartner={tradingPartner}
          tradingPartnerAddress={tradingPartnerAddress}
          selectedEmailAddresses={selectedEmailAddresses}
          setSelectedEmailAddresses={setSelectedEmailAddresses}
          tradingPartnerContactsWithEmail={tradingPartnerContactsWithEmail}
          verifiedTradingPartnerEligibility={
            verifiedTradingPartnerEligibility || null
          }
        />
      </div>

      <div className="row">
        <MedList
          customerLocationId={customerLocation?.id ?? 0}
          setMeds={setMeds}
          meds={meds}
        />
      </div>

      <div className="row">
        <AdditionalData
          additionalData={additionalData}
          setAdditionalData={setAdditionalData}
        />
      </div>

      <div className="row">
        <div className="col s12 m3">
          <a
            href="/"
            onClick={handleOpenFinalReviewModal}
            className={`btn-small blue white-text waves-effect waves-light col s12 ${
              isDisabled ? 'disabled' : ''
            }`}
          >
            Send Pedigree{meds.length > 1 ? 's' : ''}
          </a>
        </div>
      </div>

      <FinalReviewModal
        isOpen={isOpenFinalReviewModal}
        setIsOpen={setIsOpenFinalReviewModal}
        data={finalReviewData}
        send={resend}
        isBuilding={isBuilding}
      />
    </div>
  );
};

export default AmendEpcis;
